import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useFormikContext, FieldArray } from 'formik';

import { AddButton } from 'common/components/Button/AddButton';
import { Divider } from 'common/components/Divider';
import { ListPanel } from 'common/components/ListPanel';
import { getGroupSelectStatus } from 'user/utils';
import { TGroupListItem } from 'user/types';
import { ISkillGroupRequirement } from 'skill/types';

import { SkillGroupFieldListItem } from './SkillGroupFieldListItem';
import { SkillGroupSelectDrawer } from './SkillGroupSelectDrawer';

const toGroupListItem = (req: ISkillGroupRequirement): TGroupListItem =>
    req.group;

const toGroupRequirement = (
    group: TGroupListItem,
    existing?: ISkillGroupRequirement
): ISkillGroupRequirement => ({
    group,
    minAbsolute: existing?.minAbsolute ?? 0,
    minPercent: existing?.minPercent ?? 0,
});

interface IProps {
    name: string;
    value: ISkillGroupRequirement[];
}

export const SkillGroupField = ({ name, value }: IProps) => {
    const { setFieldValue } = useFormikContext();
    const [translate] = useTranslation();
    const [drawerOpen, setDrawerOpen] = useState(false);

    const addedGroupItems = value.map(toGroupListItem);

    const [selectedGroups, setSelectedGroups] =
        useState<TGroupListItem[]>(addedGroupItems);

    useEffect(() => {
        if (drawerOpen) {
            setSelectedGroups(value.map(toGroupListItem));
        }
    }, [drawerOpen, value]);

    const handleSelectGroup = (group: TGroupListItem) => {
        const newGroups = [...selectedGroups, group].sort((a, b) =>
            a.name > b.name ? 1 : -1
        );

        setSelectedGroups(newGroups);
    };

    const handleAddGroups = () => {
        const newRequirements = selectedGroups.map((group) => {
            const existing = value.find((req) => req.group?.id === group.id);

            return toGroupRequirement(group, existing);
        });

        setFieldValue(name, newRequirements);
        setDrawerOpen(false);
    };

    const handleRemoveGroup = (
        group: TGroupListItem,
        removeAdded?: boolean
    ) => {
        const { isSelected } = getGroupSelectStatus(
            selectedGroups,
            addedGroupItems,
            group
        );

        if (isSelected) {
            const newSelectedGroups = selectedGroups.filter(
                (selectedGroup) => selectedGroup.id !== group.id
            );

            setSelectedGroups(newSelectedGroups);
        }

        // When an added group is removed, it needs to be removed from the form
        if (!removeAdded) return;

        const newRequirements = value.filter(
            (req) => req.group?.id !== group.id
        );

        setFieldValue(name, newRequirements);
    };

    return (
        <FieldArray
            name={name}
            render={(arrayHelpers) => {
                const { form } = arrayHelpers;
                const addedRequirements =
                    (form.values[name] as ISkillGroupRequirement[]) || [];
                const error = form.errors[name];
                const touched = form.touched[name];
                const topLevelError =
                    typeof error === 'string' ? error : undefined;

                const addButtonHeader = (
                    <AddButton
                        aria-label={translate('skillGroupField.addButton')}
                        error={
                            !!topLevelError && touched
                                ? topLevelError
                                : undefined
                        }
                        onClick={() => setDrawerOpen(true)}
                    >
                        {translate('skillGroupField.addButton')}
                    </AddButton>
                );

                return (
                    <>
                        <ListPanel header={addButtonHeader}>
                            {!addedRequirements.length && (
                                <Typography sx={{ py: 2 }} variant="body2">
                                    {translate('skillGroupField.noGroupsAdded')}
                                </Typography>
                            )}

                            {addedRequirements.map((req, index) => (
                                <Box key={req.group?.id}>
                                    <SkillGroupFieldListItem
                                        fieldName={`${name}[${index}]`}
                                        group={req.group}
                                        onDelete={(group) => {
                                            handleRemoveGroup(group, true);
                                        }}
                                    />

                                    {index < addedRequirements.length - 1 && (
                                        <Divider />
                                    )}
                                </Box>
                            ))}
                        </ListPanel>

                        <SkillGroupSelectDrawer
                            addedGroups={addedRequirements.map(toGroupListItem)}
                            open={drawerOpen}
                            selectedGroups={selectedGroups}
                            setSelectedGroups={setSelectedGroups}
                            onAddGroups={handleAddGroups}
                            onClose={() => setDrawerOpen(false)}
                            onRemoveGroup={(group: TGroupListItem) =>
                                handleRemoveGroup(group)
                            }
                            onSelectGroup={handleSelectGroup}
                        />
                    </>
                );
            }}
        />
    );
};
